import { Extension } from "@tiptap/core";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// Paragraphs render with margin: 0, so visual spacing between paragraphs comes
// from an empty <p> acting as a spacer (see styles.ts: p:empty::after).
export const CleanPaste = Extension.create({
  name: "cleanPaste",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("cleanPaste"),
        props: {
          transformPastedHTML(html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");

            // Strip <br> only when it's a Word/Docs-style block spacer —
            // i.e. its parent has no other non-whitespace text content.
            // In-paragraph <br>s ("Best,<br>Alex" sign-offs, the assistant
            // chat's drafted templates, etc.) have sibling text and stay.
            doc.querySelectorAll("br").forEach((br) => {
              const parent = br.parentElement;
              if (!parent) return;
              const hasTextSibling = Array.from(parent.childNodes).some(
                (node) =>
                  node !== br &&
                  node.nodeType === Node.TEXT_NODE &&
                  !!node.textContent?.trim(),
              );
              if (!hasTextSibling) br.remove();
            });

            // Markdown paragraph breaks arrive as adjacent <p> siblings.
            // Insert an empty <p> between non-empty consecutive ones.
            doc.querySelectorAll("p").forEach((p) => {
              const next = p.nextElementSibling;
              if (
                next &&
                next.tagName === "P" &&
                p.textContent?.trim() &&
                next.textContent?.trim()
              ) {
                p.parentNode?.insertBefore(doc.createElement("p"), next);
              }
            });

            return doc.body.innerHTML;
          },
          clipboardTextParser(text, $context, _plain, view) {
            const { schema } = view.state;
            const dom = document.createElement("div");

            text
              .replace(/\r\n?/g, "\n")
              .split("\n")
              .forEach((line) => {
                const p = dom.appendChild(document.createElement("p"));
                if (line) p.appendChild(document.createTextNode(line));
              });

            return PMDOMParser.fromSchema(schema).parseSlice(dom, {
              preserveWhitespace: true,
              context: $context,
            });
          },
        },
      }),
    ];
  },
});
